import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/PasswordInput';

export const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password2: '',
    first_name: '',
    last_name: '',
    phone: '',
    section: 'VCN' as 'VCN' | 'HDR' | 'SANTE',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Vérifs simples côté client
    if (formData.password !== formData.password2) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Vérifier que first_name et last_name sont fournis
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError("Le prénom et le nom sont requis pour créer votre compte");
      return;
    }

    setLoading(true);

    try {
      // Le username sera généré automatiquement par le backend
      await register({ ...formData });
      navigate('/');
    } catch (err: any) {
      const data = err.response?.data;
      const extract = (val: any) => Array.isArray(val) ? val[0] : (typeof val === 'string' ? val : null);
      let friendly = null as string | null;
      if (data && typeof data === 'object') {
        const order = ['email', 'password', 'password2', 'first_name', 'last_name', 'phone', 'username', 'non_field_errors', 'detail', 'error'];
        for (const key of order) {
          if (data[key]) { friendly = extract(data[key]); break; }
        }
        if (!friendly) {
          try { friendly = JSON.stringify(data); } catch { /* noop */ }
        }
      } else if (typeof data === 'string') {
        friendly = data;
      }
      setError(friendly || 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-green-100/30 rounded-full blur-2xl"></div>
      </div>

      {/* Logo retour accueil en haut à gauche */}
      <div className="relative z-20 fixed top-4 left-4">
        <Link
          to="/"
          className="group flex items-center"
        >
          <div className="w-14 h-14 rounded-full border-[3px] border-green-600 shadow-xl overflow-hidden transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 bg-white p-1.5">
            <img
              src="/logo.jpg"
              alt="Retour à l'accueil"
              className="w-full h-full rounded-full object-contain"
              style={{ objectPosition: 'center center' }}
            />
          </div>
        </Link>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-5xl">
          {/* Layout desktop avec logo à gauche */}
          <div className="hidden lg:flex items-center gap-12 w-full">
            {/* Logo à gauche - desktop uniquement */}
            <div className="flex-shrink-0">
              <div className="w-96 h-96 rounded-full border-[3px] border-green-600 shadow-2xl overflow-hidden transform hover:scale-110 hover:rotate-3 transition-all duration-300 bg-white p-5 animate-pulse hover:animate-bounce">
                <img
                  src="/logo.jpg"
                  alt="Hanafia Logo"
                  className="w-full h-full rounded-full object-contain"
                  style={{ objectPosition: 'center center' }}
                />
              </div>
            </div>

            {/* Formulaire à droite */}
            <div className="flex-1 max-w-md ml-auto">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 p-8 sm:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              
              <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-3">
                Inscription
              </h1>
              <p className="text-gray-600 text-lg">
                Rejoignez la communauté <span className="font-semibold text-green-700">Hanafia</span>
              </p>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-down">
                  <p className="text-red-700 text-sm font-medium flex items-center space-x-2">
                    <span>⚠️</span>
                    <span>{error}</span>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    👤 Prénom
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    👤 Nom
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    placeholder="Votre nom"
                  />
                </div>
              </div>


              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📧 Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  placeholder="votre@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📱 Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  placeholder="+221 XX XXX XX XX"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🏛️ Section *
                </label>
                <select
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  required
                >
                  <option value="VCN">🎓 VCN (Université)</option>
                  <option value="HDR">🏛️ HDR</option>
                  <option value="SANTE">🩺 SANTÉ</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🔒 Mot de passe *
                  </label>
                  <PasswordInput
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🔒 Confirmation *
                  </label>
                  <PasswordInput
                    name="password2"
                    value={formData.password2}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 mt-6"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Inscription...</span>
                  </>
                ) : (
                  <>
                    <span>S'inscrire</span>
                    <span>→</span>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-green-700 font-semibold hover:text-green-800 transition-colors">
                  Se connecter
                </Link>
              </p>
            </div>
              </div>
            </div>
          </div>

          {/* Layout mobile - sans logo à gauche */}
          <div className="lg:hidden w-full max-w-md">
            {/* Card d'inscription */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 p-8 sm:p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl mb-6 shadow-lg">
                  <span className="text-4xl">✨</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-3">
                  Inscription
                </h1>
                <p className="text-gray-600 text-lg">
                  Rejoignez la communauté <span className="font-semibold text-green-700">Hanafia</span>
                </p>
              </div>

              {/* Formulaire */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-down">
                    <p className="text-red-700 text-sm font-medium flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      👤 Prénom
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      👤 Nom
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                      placeholder="Votre nom"
                    />
                  </div>
                </div>


                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📧 Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📱 Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    placeholder="+221 XX XXX XX XX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🏛️ Section *
                  </label>
                  <select
                    name="section"
                    value={formData.section}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    required
                  >
                    <option value="VCN">🎓 VCN (Université)</option>
                    <option value="HDR">🏛️ HDR</option>
                    <option value="SANTE">🩺 SANTÉ</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔒 Mot de passe *
                    </label>
                    <PasswordInput
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔒 Confirmation *
                    </label>
                    <PasswordInput
                      name="password2"
                      value={formData.password2}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 mt-6"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Inscription...</span>
                    </>
                  ) : (
                    <>
                      <span>S'inscrire</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="mt-8 text-center">
                <p className="text-gray-600 text-sm">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-green-700 font-semibold hover:text-green-800 transition-colors">
                    Se connecter
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
